import type { CustomModuleBaseTypeEnum } from '~/shared/api'
import { ProjectModuleSlugEnum } from '~/shared/const'
import { VUESAX_ICONS_DICTIONARY } from '~/shared/const/vuesaxIconsDictionary'
import { normalizeWorkspaceModuleSlug } from './workspaceModuleVisibility'

export interface ILandingTemplateButton {
  moduleSlug: string
  label: string
  color: string
  moduleObjectId?: string | null
  moduleType?: CustomModuleBaseTypeEnum | null
}

export type LandingTemplateButtonsMap = Record<string, ILandingTemplateButton | null | undefined>

export interface LandingTemplateEditDraft {
  formModel: Record<string, unknown>
  images: string[]
  buttons: LandingTemplateButtonsMap
}

export interface LandingProjectModuleOption {
  moduleSlug: string
  customModuleId?: string
  moduleType?: CustomModuleBaseTypeEnum
  title: string
  icon: string
  previewColor?: string
  previewIconColor?: string
}

export const ONE_TEMPLATE_BUTTON_SLOTS = [
  'header',
  'section',
  'leftBlock',
  'rightBlock',
] as const

export const TWO_TEMPLATE_BUTTON_SLOTS = [
  'header',
  'leftHeader',
  'rightHeader',
  'additional',
] as const

export const THREE_TEMPLATE_BUTTON_SLOTS = [
  'header',
  'leftHeader',
  'centerHeader',
  'rightHeader',
  'additional',
] as const

export const FOUR_TEMPLATE_BUTTON_SLOTS = [
  'mainText',
  'secondHeader',
  'firstSection',
  'secondSection',
  'thirdSection',
  'additional',
] as const

export const FIVE_TEMPLATE_BUTTON_SLOTS = [
  'header',
  'leftSection',
  'rightSection',
  'additional',
  'secondaryAdditional',
] as const

export const LANDING_NATIVE_MODULE_CATALOG: Record<string, { title: string, icon: string }> = {
  [ProjectModuleSlugEnum.AboutProject]: {
    title: 'О проекте',
    icon: 'vuesax:slider-horizontal-twotone',
  },
  [ProjectModuleSlugEnum.ProjectMembers]: {
    title: 'Участники',
    icon: 'vuesax:people-twotone',
  },
  [ProjectModuleSlugEnum.ProjectManagement]: {
    title: 'Управление',
    icon: 'vuesax:mouse-square-twotone',
  },
  [ProjectModuleSlugEnum.Supporters]: {
    title: 'Реестр',
    icon: 'vuesax:profile-tick-twotone',
  },
  [ProjectModuleSlugEnum.ProjectNews]: {
    title: 'Новости',
    icon: 'vuesax:firstline',
  },
  [ProjectModuleSlugEnum.ProjectSurvey]: {
    title: 'Опросы',
    icon: 'vuesax:message-question-twotone',
  },
  [ProjectModuleSlugEnum.ProjectOrgMail]: {
    title: 'Написать организаторам проекта',
    icon: 'vuesax:messages',
  },
  [ProjectModuleSlugEnum.ProjectSpeeches]: {
    title: 'Выступления',
    icon: 'vuesax:microphone-2-twotone',
  },
  question_to_deputy: {
    title: 'Вопрос депутату',
    icon: 'vuesax:direct-inbox-twotone',
  },
  service_phones: {
    title: 'Телефоны служб',
    icon: 'vuesax:bank',
  },
  project_counters: {
    title: 'Счётчики',
    icon: 'vuesax:home-1-twotone',
  },
  consumer_rights: {
    title: 'Права потребителей',
    icon: 'vuesax:money-4-twotone',
  },
  accident_guidance: {
    title: 'Действия при ЧС',
    icon: 'vuesax:category-2-twotone',
  },
  entertainment: {
    title: 'Развлечения',
    icon: 'vuesax:musicnote-twotone',
  },
  [ProjectModuleSlugEnum.ProjectCalendar]: {
    title: 'Календарь',
    icon: 'vuesax:calendar-8-linear',
  },
}

export const LANDING_MODULE_FALLBACK_ICON = 'vuesax:element-4-twotone'

const VUESAX_ICON_IDS = new Set<string>(VUESAX_ICONS_DICTIONARY)

export function isLandingPlacementButtonConfigured(
  button: ILandingTemplateButton,
): boolean {
  return Boolean(button.moduleType)
}

export function resolveLandingModuleIcon(
  moduleSlug: string,
  icon?: string | null,
): string {
  const trimmed = icon?.trim()
  if (trimmed) {
    const normalizedIcon = trimmed.startsWith('vuesax:')
      ? trimmed
      : `vuesax:${trimmed}`
    if (VUESAX_ICON_IDS.has(normalizedIcon)) {
      return normalizedIcon
    }
  }

  const catalogIcon = LANDING_NATIVE_MODULE_CATALOG[
    normalizeWorkspaceModuleSlug(moduleSlug)
  ]?.icon

  if (catalogIcon && VUESAX_ICON_IDS.has(catalogIcon)) {
    return catalogIcon
  }

  return LANDING_MODULE_FALLBACK_ICON
}

export function buildLandingTemplateDraftStorageKey(
  entityId: string,
  templateNumericId: string,
) {
  return `landing-template-edit-draft:${entityId}:${templateNumericId}`
}

function normalizeLandingTemplateButton(
  button?: ILandingTemplateButton | null,
): ILandingTemplateButton | null {
  if (!button?.moduleSlug?.trim() || !button.label?.trim() || !button.color?.trim()) {
    return null
  }

  const normalized: ILandingTemplateButton = {
    moduleSlug: normalizeWorkspaceModuleSlug(button.moduleSlug.trim()),
    label: button.label.trim(),
    color: button.color.trim(),
    ...(button.moduleObjectId?.trim()
      ? { moduleObjectId: button.moduleObjectId.trim() }
      : {}),
    ...(button.moduleType
      ? { moduleType: button.moduleType }
      : {}),
  }

  if (!isLandingPlacementButtonConfigured(normalized)) {
    return null
  }

  return normalized
}

export function normalizeLandingTemplateButtons(
  buttons?: LandingTemplateButtonsMap | null,
): LandingTemplateButtonsMap {
  if (!buttons || typeof buttons !== 'object') {
    return {}
  }

  const result: LandingTemplateButtonsMap = {}

  for (const [slot, button] of Object.entries(buttons)) {
    const normalized = normalizeLandingTemplateButton(button)
    if (normalized) {
      result[slot] = normalized
    }
  }

  return result
}

export function serializeLandingTemplateButtonsForSave(
  buttons?: LandingTemplateButtonsMap | null,
): LandingTemplateButtonsMap {
  return normalizeLandingTemplateButtons(buttons)
}

export function collectLandingTemplateButtons(
  buttons?: LandingTemplateButtonsMap | null,
): ILandingTemplateButton[] {
  if (!buttons) {
    return []
  }

  const seen = new Set<string>()
  const result: ILandingTemplateButton[] = []

  for (const button of Object.values(buttons)) {
    if (!button) {
      continue
    }

    const key = button.moduleObjectId?.trim()
      ? `${normalizeWorkspaceModuleSlug(button.moduleSlug)}:${button.moduleObjectId.trim()}`
      : normalizeWorkspaceModuleSlug(button.moduleSlug)
    if (seen.has(key)) {
      continue
    }

    seen.add(key)
    result.push(button)
  }

  return result
}
